import { isEmpty, isNil } from "lodash";
import { Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { useEffect, useMemo } from "react";

import { getOriginalResource } from "../../../helpers";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";
import { getRequestsData, RequestData } from "../get_request_data";
import { RowChildrenItem, useRowChildren } from "../queries/use_row_children";
import { checkIsLastLevel } from "../side_panel_row.utils";
import { useInsertChildrenRows } from "./use_insert_children_rows";

interface UseChildrenRows {
  row: Row;
  expanded: boolean;
  currentPage: number;
  onSuccess: (data: RowChildrenItem, requestData: RequestData) => void;
}
interface UseChildrenRowsReturn {
  pageableChildrenMeta: RowChildrenItem["meta"];
  childrenCount: number;
  loading: boolean;
}

const shouldNotInsertChildrenRows = (
  rowChildrenData: RowChildrenItem | undefined,
  opened: boolean,
  currentPage?: number
) => !rowChildrenData || !opened || (!isNil(currentPage) && currentPage > rowChildrenData.meta.totalPages);

export const useChildrenRows = ({
  row,
  expanded,
  currentPage,
  onSuccess
}: UseChildrenRows): UseChildrenRowsReturn => {
  const {
    calendarMode,
    calendarData: {
      data: { meta }
    }
  } = useBookingsOverviewContext();
  const insertChildrenRows = useInsertChildrenRows({ row });

  const { type } = row;
  const originalResourceId = getOriginalResource(row, calendarMode)?.id;
  const isLastLevel = checkIsLastLevel(calendarMode, row);
  const requestData = useMemo(
    () => (!isLastLevel ? getRequestsData(calendarMode, row, meta.filters) : []),
    [calendarMode, row, isLastLevel, meta.filters]
  );

  const enabled = expanded && !isLastLevel;

  const {
    childrenByType: rowChildrenByType,
    isLoading: isChildrenRowsLoading,
    isFetching: isChildrenRowsFetching
  } = useRowChildren({
    requestData,
    type,
    mode: calendarMode,
    id: !isLastLevel ? originalResourceId! : -1,
    page: currentPage,
    enabled,
    onSuccess
  });

  const { pageableRowChildren, nonPageableRowChildren } = useMemo(() => {
    if (type === RowType.Engagement) {
      return {
        pageableRowChildren: rowChildrenByType.role,
        nonPageableRowChildren: rowChildrenByType.profile
      };
    } else {
      return {
        pageableRowChildren: Object.values(rowChildrenByType)[0],
        nonPageableRowChildren: undefined
      };
    }
  }, [rowChildrenByType, type]);

  const isRowsLoading = isChildrenRowsLoading || isChildrenRowsFetching;
  const pageableChildrenMeta = pageableRowChildren?.meta || { page: 1, total: 0 };

  useEffect(() => {
    const childrenRowsToInsert = [
      ...(shouldNotInsertChildrenRows(nonPageableRowChildren, expanded)
        ? []
        : nonPageableRowChildren!.entries),
      ...(shouldNotInsertChildrenRows(pageableRowChildren, expanded, currentPage)
        ? []
        : pageableRowChildren.entries)
    ];
    if (isEmpty(childrenRowsToInsert) || isChildrenRowsFetching) {
      return;
    }
    insertChildrenRows(childrenRowsToInsert);
  }, [pageableRowChildren, nonPageableRowChildren, expanded, isChildrenRowsFetching]);

  return {
    pageableChildrenMeta,
    loading: enabled && isRowsLoading,
    childrenCount: pageableChildrenMeta.total + (nonPageableRowChildren?.meta?.total || 0)
  };
};
